import { ComponentChildren, Fragment, h } from "preact";
import { useRef, useEffect, useState } from "preact/hooks";

import classname from "../../../../../../../foundation/utils/classname";

import DragDropContainer from "../../../../../../../library/DragDropContainer";
import Button, { ButtonVariant } from "../../../../../../../library/v2/Button";
import IconArrowLeft from "../../../../../../../library/v2/Icons/IconArrowLeft";
import IconArrowRight from "../../../../../../../library/v2/Icons/IconArrowRight";
import IconLink from "../../../../../../../library/v2/Icons/IconLink";
import IconMenu from "../../../../../../../library/v2/Icons/IconMenu";
import IconPlusInCircle from "../../../../../../../library/v2/Icons/IconPlusInCircle";
import Menu from "../../../../../../../library/v2/Menu";
import { OverlayMenuHorizontalAligment } from "../../../../../../../library/v2/Menu/OverlayMenu";
import OnlyIconButton, { OnlyIconButtonVariant } from "../../../../../../../library/v2/OnlyIconButton";

import { BoardTabBarItemProps, BoardTabBarItemReorderPosition } from "./BoardTabBarItem";
import DragDropBoardTabBarItem from "./DragDropBoardTabBarItem";

import styles from "./BoardTabBar.module.css";

const HEIGHT = 32;
const ITEM_CONTEXT_MENU_OFFSET_X = 4;
const ITEM_CONTEXT_MENU_OFFSET_Y = 4;
const BUTTON_MENU_OFFSET_Y = 8;
const MENU_OVERLAY_OFFSET = 8;
const SCROLL_BUTTON_WIDTH = 24;

enum MenuSource {
    Item = "item",
    AccessoryButton = "button"
}

interface Props {
    tabs: BoardTabBarItemProps["tab"][];
    selectedTabId: string;
    creatingTabId?: string;
    accessoryContent?: ComponentChildren;
    canManageBoards: boolean;
    onSelectTab?: (tabId: string) => void;
    onRenameBoard?: (tabId: string, name: string) => void;
    onCopyBoardLink?: (tabId: string) => void;
    onExportBoard?: () => void;
    onDeleteBoard?: (tabId: string) => void;
    onReorderBoard?: (tabId: string, fromIndex: number, toIndex: number) => void;
    onCreateBoard?: () => void;
    onSubmitCreateBoard?: (tabId: string, name?: string) => void;
}

export default function BoardTabBar({
    tabs,
    selectedTabId,
    creatingTabId,
    accessoryContent,
    canManageBoards,
    onSelectTab,
    onRenameBoard,
    onCopyBoardLink,
    onExportBoard,
    onDeleteBoard,
    onReorderBoard,
    onCreateBoard,
    onSubmitCreateBoard
}: Props) {
    const [renamingTabId, setRenamingTabId] = useState<string | undefined>(undefined);
    const [menu, setMenu] = useState<{
        tabId: string;
        origin: { x: number; y: number; };
        source: MenuSource;
    } | undefined>(undefined);
    const [reorderingTabIds, setReorderingTabIds] = useState<string[] | undefined>(undefined);
    const [isLeftScrollButtonVisible, setLeftScrollButtonVisible] = useState(false);
    const [isRightScrollButtonVisible, setRightScrollButtonVisible] = useState(false);

    const dragDropContainerRef = useRef<DragDropContainer>(null);
    const leftScrollAFRef = useRef<number>();
    const rightScrollAFRef = useRef<number>();

    useEffect(() => {
        if (creatingTabId === undefined) {
            return;
        }

        setRenamingTabId(creatingTabId);
        dragDropContainerRef.current?.scrollToEnd();
    }, [creatingTabId]);

    useEffect(() => {
        if (isLeftScrollButtonVisible === false) {
            window.cancelAnimationFrame(leftScrollAFRef.current);
        }
    }, [isLeftScrollButtonVisible]);

    useEffect(() => {
        if (isRightScrollButtonVisible === false) {
            window.cancelAnimationFrame(rightScrollAFRef.current);
        }
    }, [isRightScrollButtonVisible]);

    function handleClickShareButton() {
        onCopyBoardLink?.(selectedTabId);
    }

    function handleClickMenuButton(event: MouseEvent) {
        const target = event.currentTarget as HTMLDivElement;
        const rect = target.getBoundingClientRect();

        setMenu({
            tabId: selectedTabId,
            origin: {
                x: rect.right,
                y: rect.bottom + BUTTON_MENU_OFFSET_Y
            },
            source: MenuSource.AccessoryButton
        });
    }

    function handleCloseMenu() {
        setMenu(undefined);
    }

    function handleRenameBoard() {
        if (!menu) {
            return;
        }

        setRenamingTabId(menu.tabId);
    }

    function handleCopyBoardLink() {
        if (!menu) {
            return;
        }

        onCopyBoardLink?.(menu.tabId);
    }

    function handleDeleteBoard() {
        if (!menu) {
            return;
        }

        onDeleteBoard?.(menu.tabId);
    }

    function handleStartDrag() {
        setReorderingTabIds(tabs.map(tab => tab.id));
    }

    function handleEndDrag() {
        setReorderingTabIds(undefined);
    }

    function handleCanScroll(canScroll: boolean) {
        setRightScrollButtonVisible(canScroll);
    }

    function handleScroll(event: Event) {
        const target = event.target as HTMLElement;

        setLeftScrollButtonVisible(target.scrollLeft !== 0);

        if (isRightScrollButtonVisible) {
            setRightScrollButtonVisible(
                target.scrollLeft + target.clientWidth + SCROLL_BUTTON_WIDTH < target.scrollWidth
            );
        } else {
            setRightScrollButtonVisible(target.scrollLeft + target.clientWidth < target.scrollWidth);
        }
    }

    function scrollLeft() {
        window.cancelAnimationFrame(leftScrollAFRef.current);
        dragDropContainerRef.current?.scrollLeft();
        leftScrollAFRef.current = window.requestAnimationFrame(scrollLeft);
    }

    function scrollRight() {
        window.cancelAnimationFrame(rightScrollAFRef.current);
        dragDropContainerRef.current?.scrollRight();
        rightScrollAFRef.current = window.requestAnimationFrame(scrollRight);
    }

    function endScrollLeft() {
        window.cancelAnimationFrame(leftScrollAFRef.current);
    }

    function endScrollRight() {
        window.cancelAnimationFrame(rightScrollAFRef.current);
    }

    function renderMenu() {
        if (!menu) {
            return null;
        }

        const horizontalAlignment = menu.source === MenuSource.Item
            ? OverlayMenuHorizontalAligment.Left
            : OverlayMenuHorizontalAligment.Right;

        return (
            <Menu.Overlay
                horizontalAlignment={horizontalAlignment}
                clickOrigin={menu.origin}
                offset={MENU_OVERLAY_OFFSET}
                onClose={handleCloseMenu}>
                {canManageBoards && (
                    <>
                        <Menu.Button onClick={handleRenameBoard}>Rename board</Menu.Button>
                        <Menu.Divider/>
                    </>
                )}
                <Menu.Button onClick={handleCopyBoardLink}>Copy board link</Menu.Button>
                {menu.source === MenuSource.AccessoryButton && (
                    <Menu.Button onClick={onExportBoard}>Export board as PDF</Menu.Button>
                )}
                {canManageBoards && (
                    <>
                        <Menu.Divider/>
                        <Menu.Button warning onClick={handleDeleteBoard}>Delete board</Menu.Button>
                    </>
                )}
            </Menu.Overlay>
        );
    }

    return (
        <div class={classname(styles.boardTabBar, {
            [styles.hasCreateButton]: canManageBoards
        })}>
            <DragDropContainer
                ref={dragDropContainerRef}
                class={styles.itemsWrapper}
                itemsClass={classname(styles.items, {
                    [styles.leftScrollButtonVisible]: isLeftScrollButtonVisible
                })}
                disabled={!canManageBoards || creatingTabId !== undefined}
                onStartDrag={handleStartDrag}
                onEndDrag={handleEndDrag}
                onScroll={handleScroll}
                onCanScroll={handleCanScroll}>
                {isLeftScrollButtonVisible && (
                    <div class={styles.leftScrollButtonWrapper}>
                        <OnlyIconButton
                            class={styles.leftScrollButton}
                            icon={IconArrowLeft}
                            variant={OnlyIconButtonVariant.Ninja}
                            onMouseDown={scrollLeft}
                            onMouseUp={endScrollLeft}
                            onMouseLeave={endScrollLeft}/>
                    </div>
                )}
                {tabs.map((tab, index) => {
                    const isSelected = tab.id === selectedTabId;
                    const isNextTabSelected = index !== tabs.length - 1 && tabs[index + 1].id === selectedTabId;
                    const isRenaming = tab.id === renamingTabId;
                    const isCreating = tab.id === creatingTabId;

                    const canSelect = !isSelected && !isCreating;

                    function handleSelect() {
                        if (!canSelect) {
                            return;
                        }

                        onSelectTab?.(tab.id);
                    }

                    function handleContextMenu(event: MouseEvent) {
                        if (isCreating) {
                            return;
                        }

                        event.preventDefault();

                        const target = event.currentTarget as HTMLDivElement;
                        const rect = target.getBoundingClientRect();

                        setMenu({
                            tabId: tab.id,
                            origin: {
                                x: rect.left + ITEM_CONTEXT_MENU_OFFSET_X,
                                y: rect.bottom + ITEM_CONTEXT_MENU_OFFSET_Y
                            },
                            source: MenuSource.Item
                        });
                    }

                    function handleSubmitRenameBoard(newName?: string) {
                        if (tab.id.startsWith("newBoard")) {
                            onSubmitCreateBoard?.(tab.id, newName);

                            setRenamingTabId(undefined);

                            return;
                        }

                        if (newName && newName !== tab.name) {
                            onRenameBoard?.(tab.id, newName);
                        }

                        setRenamingTabId(undefined);
                    }

                    function handleReorderHoverBoard(fromTabId: string, position: BoardTabBarItemReorderPosition) {
                        const fromIndex = tabs.findIndex(someTab => someTab.id === fromTabId);

                        let toIndex = index;
                        if (index > fromIndex) {
                            toIndex = position === BoardTabBarItemReorderPosition.Left ? index - 1 : index;
                        } else if (index < fromIndex) {
                            toIndex = position === BoardTabBarItemReorderPosition.Right ? index + 1 : index;
                        }

                        const reorderingTabs = [...tabs];
                        reorderingTabs.splice(toIndex, 0, reorderingTabs.splice(fromIndex, 1)[0]);
                        const newReorderingTabIds = reorderingTabs.map(someTab => someTab.id);

                        setReorderingTabIds(newReorderingTabIds);
                    }

                    function handleReorderDropBoard(fromTabId: string) {
                        if (!reorderingTabIds) {
                            return;
                        }

                        const fromIndex = tabs.findIndex(someTab => someTab.id === fromTabId);
                        const toIndex = reorderingTabIds.indexOf(fromTabId);
                        if (fromIndex === toIndex) {
                            return;
                        }

                        onReorderBoard?.(fromTabId, fromIndex, toIndex);
                    }

                    let reorderingIndexDelta: number | undefined;
                    if (reorderingTabIds) {
                        const reorderingIndex = reorderingTabIds.indexOf(tab.id);
                        reorderingIndexDelta = reorderingIndex - index;
                    }

                    return (
                        <DragDropBoardTabBarItem
                            key={tab.id}
                            tab={tab}
                            isSelected={isSelected}
                            isNextTabSelected={isNextTabSelected}
                            isRenaming={isRenaming}
                            isContextMenuOpen={menu && menu.source === MenuSource.Item && menu.tabId === tab.id}
                            reorderProps={reorderingIndexDelta !== undefined
                                ? { indexDelta: reorderingIndexDelta }
                                : undefined}
                            onSelect={handleSelect}
                            onContextMenu={handleContextMenu}
                            onSubmitRename={handleSubmitRenameBoard}
                            onReorderHover={handleReorderHoverBoard}
                            onReorderDrop={handleReorderDropBoard}/>
                    );
                })}
                {isRightScrollButtonVisible && (
                    <div class={styles.rightScrollButtonWrapper}>
                        <OnlyIconButton
                            class={styles.rightScrollButton}
                            icon={IconArrowRight}
                            variant={OnlyIconButtonVariant.Ninja}
                            onMouseDown={scrollRight}
                            onMouseUp={endScrollRight}
                            onMouseLeave={endScrollRight}/>
                    </div>
                )}
                {canManageBoards && (
                    <div
                        class={classname(styles.createButtonWrapper, {
                            [styles.elevated]: isRightScrollButtonVisible
                        })}>
                        <OnlyIconButton
                            class={styles.createButton}
                            icon={IconPlusInCircle}
                            variant={OnlyIconButtonVariant.Ninja}
                            disabled={creatingTabId !== undefined}
                            onClick={onCreateBoard}/>
                    </div>
                )}
            </DragDropContainer>
            <div class={styles.accessoryContentContainer}>
                <Button
                    class={styles.shareButton}
                    variant={ButtonVariant.Ninja}
                    icon={IconLink}
                    onClick={handleClickShareButton}>
                    Share
                </Button>
                {accessoryContent}
                {canManageBoards && (
                    <OnlyIconButton
                        class={classname(styles.menuButton, {
                            [styles.isOpen]: menu && menu.source === MenuSource.AccessoryButton
                        })}
                        icon={IconMenu}
                        variant={OnlyIconButtonVariant.Ninja}
                        onClick={handleClickMenuButton}/>
                )}
            </div>
            {renderMenu()}
        </div>
    );
}

export { HEIGHT as BOARD_TAB_BAR_HEIGHT };
